import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { bookingContent, clinicInfo } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Book = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const payload = {
      page: "Contact", // change to "Pharmacy" on Pharmacy page, etc.
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      submittedAt: new Date().toISOString(),
    };

    const res = await fetch("https://formspree.io/f/mvzagdog", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Form submission failed");

    alert("Thanks! Your message has been sent.");
    setFormData({ name: "", email: "", phone: "", message: "" });
  } catch (err) {
    console.error(err);
    alert("Sorry — something went wrong. Please try again or call the clinic.");
  } finally {
    setIsSubmitting(false);
  }
};

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#000053] mb-6">
              {bookingContent.title}
            </h1>
            <p className="text-xl text-[#36454F] mb-4">
              {bookingContent.subtitle}
            </p>
            <p className="text-lg text-gray-600">
              {bookingContent.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods & Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Methods */}
            <div>
              <h2 className="text-2xl font-bold text-[#000053] mb-8">Contact Methods</h2>
              <div className="space-y-6">
                {bookingContent.methods.map((method, index) => (
                  <Card key={index} className="hover:shadow-lg transition-all border-2 hover:border-[#006D73]">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="h-12 w-12 bg-[#006D73] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                          {method.icon === 'phone' && <Phone className="h-6 w-6 text-[#006D73]" />}
                          {method.icon === 'mail' && <Mail className="h-6 w-6 text-[#006D73]" />}
                          {method.icon === 'map-pin' && <MapPin className="h-6 w-6 text-[#006D73]" />}
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#000053] mb-2">{method.title}</h3>
                          <p className="text-[#006D73] font-medium mb-1">{method.value}</p>
                          <p className="text-gray-600 text-sm">{method.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Booking Form */}
            <div>
              <h2 className="text-2xl font-bold text-[#000053] mb-8">Request an Appointment</h2>
              <Card className="border-2 border-gray-200">
                <CardHeader>
                  <CardTitle className="text-[#000053]">Fill out the form below</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-[#36454F]">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2 focus:border-[#006D73] focus:ring-[#006D73]"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#36454F]">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2 focus:border-[#006D73] focus:ring-[#006D73]"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-[#36454F]">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-2 focus:border-[#006D73] focus:ring-[#006D73]"
                        placeholder="519-XXX-XXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-[#36454F]">Service *</Label>
                      <Select onValueChange={handleServiceChange} value={formData.service} required>
                        <SelectTrigger className="mt-2 focus:border-[#006D73] focus:ring-[#006D73]">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="family-practice">Family Practice</SelectItem>
                          <SelectItem value="pharmacy">Pharmacy</SelectItem>
                          <SelectItem value="physiotherapy">Physiotherapy</SelectItem>
                          <SelectItem value="chiropractic">Chiropractic</SelectItem>
                          <SelectItem value="massage-therapy">Massage Therapy</SelectItem>
                          <SelectItem value="custom-orthotics">Custom Orthotics</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#36454F]">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="mt-2 focus:border-[#006D73] focus:ring-[#006D73]"
                        placeholder="Please let us know your preferred date and time, or any questions you have..."
                      />
                    </div>

                    <Button type="submit" disabled={isSubmitting} className="w-full bg-[#006D73] text-white hover:bg-[#005a5f] transition-all" size="lg">
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Book;